import i18n from "@/i18n";
import { ApiApprovalRequest, ApiApprovalRequestStatus } from "@/types/approvals/approval.types.ts";
import { ApiCurrency } from "@/types/currency.types.ts";
import {
  ApiCustomField,
  ApiCustomFieldValue,
  ApiField,
  ApiFieldValueTypes,
  ApiSection,
} from "@/types/custom-field.types.ts";
import { ApiEmployee, ApiEmployeeShort } from "@/types/employees/employee.types.ts";
import { ApiFilterBase, ApiPagedData } from "@skailer-com/uikit/core";
import { ApiLegalEntity } from "@/types/legal-entity.types.ts";
import { ApiLocation } from "@/types/location.types.ts";
import { ApiApplicantShort, ApiCandidateShort } from "@/types/recruit/applicant.types.ts";
import { SelectItem } from "@skailer-com/uikit";
import { createContext, useContext } from "react";
import { UseFormReturn } from "react-hook-form";
import { z } from "zod";

export interface ApiOffer {
  id: number;
  first_name: string;
  last_name: string;
  job_title: string;
  date_start: Date;
  legal_entity: ApiLegalEntity;
  location: ApiLocation;
  manager?: ApiEmployee;
  salary?: ApiOfferSalary;
  offer_text: string;
  candidate: ApiCandidateShort;
  applicant: ApiApplicantShort;
  config: ApiOfferConfig;
  created_by: ApiEmployeeShort;
  is_archived: boolean;
  datetime_created_at: Date;
  status: ApiApprovalRequestStatus;
  uuid: string;
  organization: {
    title: string;
    logo_url: string;
  };
  custom_fields?: ApiCustomField[];
}

export interface ApiOfferPublicCustomField {
  slug: string;
  title: string;
  field_type: ApiFieldValueTypes;
  value: string;
  url: string | null;
}

export interface ApiOfferPublicField {
  slug: string;
  title: string;
}

export interface ApiOfferPublicSection {
  title: string;
  fields: ApiOfferPublicField[];
}

export interface ApiOfferPublic {
  first_name: string;
  last_name: string;
  job_title: string | null;
  date_start: Date | null;
  legal_entity: Pick<ApiLegalEntity, "title"> | null;
  location: Pick<ApiLocation, "title"> | null;
  manager: ApiEmployeeShort | null;
  salary: ApiOfferSalary | null;
  offer_text: string | null;
  organization: {
    title: string | null;
    logo_url: string | null;
  };
  custom_fields: ApiOfferPublicCustomField[];
  sections: ApiOfferPublicSection[];
}

export interface ApiOfferRequest extends ApiApprovalRequest {
  offer_data: ApiOffer;
}

export interface ApiPagedOfferData extends ApiPagedData<ApiOfferRequest> {
  awaiting_count: number;
}

export interface ApiOfferApproveRequest {
  is_bypass_approval_flow?: boolean;
  comment?: string | null;
}

export interface ApiOfferConfirmationCommentUpdate {
  comment: string | null;
}

export interface ApiOfferSavePayload {
  first_name: string;
  last_name: string;
  job_title?: string | null;
  date_start?: Date | null;
  legal_entity_id?: number | null;
  location_id?: number | null;
  manager_id?: number | null;
  salary?: ApiOfferSalarySave | null;
  offer_text?: string | null;
  candidate_id?: number | null;
  config?: ApiOfferConfig | null;
  custom_fields?: Record<string, ApiCustomFieldValue>;
}

export interface ApiOfferConfig {
  hide_location: boolean;
  hide_legal_entity: boolean;
  hide_manager: boolean;
  hide_salary: boolean;
  visibility: ApiOfferVisibilityEnum[];
  is_public: boolean;
  hidden_custom_field_ids: number[];
}

export interface ApiOfferSalary {
  salary_type: ApiCompensationTypeEnum;
  salary_value: string;
  currency: ApiCurrency | null;
}

export interface ApiOfferSalarySave {
  salary_type: ApiCompensationTypeEnum | null;
  salary_value: string | null;
  currency_code: string | null;
}

export enum ApiOfferVisibilityEnum {
  only_me_admins_approvers = "only_me_admins_approvers",
  recruiters = "recruiters",
  stakeholders = "stakeholders",
  all = "all",
}

export enum ApiCompensationTypeEnum {
  annual_gross = "annual_gross",
  annual_net = "annual_net",
  monthly_gross = "monthly_gross",
  monthly_net = "monthly_net",
  custom = "custom",
}

export const getOfferSalaryTypeTitle = (type: ApiCompensationTypeEnum): string => {
  const titles: Record<ApiCompensationTypeEnum, string> = {
    [ApiCompensationTypeEnum.annual_gross]: i18n.t("ats:offer_salary_annual_gross"),
    [ApiCompensationTypeEnum.annual_net]: i18n.t("ats:offer_salary_annual_net"),
    [ApiCompensationTypeEnum.monthly_gross]: i18n.t("ats:offer_salary_monthly_gross"),
    [ApiCompensationTypeEnum.monthly_net]: i18n.t("ats:offer_salary_monthly_net"),
    [ApiCompensationTypeEnum.custom]: i18n.t("common:custom"),
  };

  return titles[type];
};

export const getOfferSalaryVisibilityTitle = (type: ApiOfferVisibilityEnum): string => {
  const titles: Record<ApiOfferVisibilityEnum, string> = {
    [ApiOfferVisibilityEnum.only_me_admins_approvers]: i18n.t("common:only_me_admins_approvers"),
    [ApiOfferVisibilityEnum.recruiters]: i18n.t("ats:recruiters"),
    [ApiOfferVisibilityEnum.stakeholders]: i18n.t("ats:stakeholders"),
    [ApiOfferVisibilityEnum.all]: i18n.t("common:all"),
  };

  return titles[type];
};

export const getOfferSalaryTypes = (): SelectItem[] => {
  return [
    ApiCompensationTypeEnum.annual_gross,
    ApiCompensationTypeEnum.annual_net,
    ApiCompensationTypeEnum.monthly_gross,
    ApiCompensationTypeEnum.monthly_net,
    ApiCompensationTypeEnum.custom,
  ].map((item) => ({
    id: item,
    title: getOfferSalaryTypeTitle(item),
  }));
};

export const offerSalarySchema = z
  .object({
    salary_type: z
      .enum(ApiCompensationTypeEnum, { error: i18n.t("errors:invalid_selection") })
      .nullable(),
    salary_value: z
      .string()
      .nullable()
      .transform((value) => (typeof value === "string" ? value.trim() : value)),
    currency_code: z.string().nullable(),
  })
  .superRefine((data, ctx) => {
    const { salary_type, salary_value, currency_code } = data;
    const hasAnySalaryField = !!salary_type || !!salary_value || !!currency_code;

    if (!hasAnySalaryField) return;

    if (!salary_type) {
      ctx.addIssue({
        code: "custom",
        message: i18n.t("errors:field_required"),
        path: ["salary_type"],
      });
      return;
    }

    if (!salary_value) {
      ctx.addIssue({
        code: "custom",
        message: i18n.t("errors:field_required"),
        path: ["salary_value"],
      });
      return;
    }

    if (salary_type !== ApiCompensationTypeEnum.custom && salary_value) {
      const isValidNumber = /^\d+([.,]\d{1,2})?$/.test(salary_value);
      if (!isValidNumber) {
        ctx.addIssue({
          code: "custom",
          message: i18n.t("errors:invalid_decimal_format"),
          path: ["salary_value"],
        });
        return;
      }
    }

    if (salary_type !== ApiCompensationTypeEnum.custom && !currency_code) {
      ctx.addIssue({
        code: "custom",
        message: i18n.t("errors:field_required"),
        path: ["currency_code"],
      });
    }
  })
  .nullable()
  .transform((value) => {
    if (value && !value.salary_type && !value.salary_value && !value.currency_code) {
      return null;
    }
    return value;
  });

export const offerConfigSchema = z.object({
  hide_location: z.boolean().nullable(),
  hide_legal_entity: z.boolean().nullable(),
  hide_manager: z.boolean().nullable(),
  hide_salary: z.boolean().nullable(),
  visibility: z.array(z.string()).nullable(),
  is_public: z.boolean().nullable(),
  hidden_custom_field_ids: z.array(z.number()),
});

export interface OfferEditSystemFormData {
  first_name: string;
  last_name: string;
  job_title: string | null;
  date_start: Date | null;
  legal_entity_id: number | null;
  location_id: number | null;
  manager_id: number | null;
  salary: ApiOfferSalarySave | null;
  config: z.infer<typeof offerConfigSchema>;
  offer_template_id: number | null;
  offer_text: string;
}

export type OfferEditFormData = OfferEditSystemFormData & Record<string, unknown>;

export type OfferFormSection = ApiSection & { fields: ApiField[] };

export interface ApiOffersFilter extends ApiFilterBase {
  text?: string;
  statuses?: ApiApprovalRequestStatus[];
  manager_ids?: number[];
  created_by_ids?: number[];
  page_size?: number;
}

export enum OfferSteps {
  details = 1,
  preview = 2,
}

export enum ApiOfferFileTypes {
  PDF = "pdf",
}

interface OfferEditModalContext {
  id?: string;
  candidateId?: number;
  employeeId?: number;
  applicant?: ApiApplicantShort;
  offerRequest?: ApiOfferRequest;
  formHook: UseFormReturn<OfferEditFormData>;
  sections: OfferFormSection[];
  formFields: ApiField[];
  offerConfig: ApiOfferConfig;
  handleUpdateConfig: (
    key: keyof ApiOfferConfig,
    value: boolean | ApiOfferVisibilityEnum[] | number[]
  ) => void;
}

export const OfferEditModalContext = createContext<OfferEditModalContext | undefined>(undefined);

export const useOfferEditContext = () => {
  const context = useContext(OfferEditModalContext);
  if (!context) {
    throw new Error("useBlogEditContext must be used within a OfferEditModalContextProvider");
  }
  return context;
};
